/**
 * EventStreamContext — single shared SSE connection for the entire app.
 *
 * Replaces the per-component useEventStream() hook with a context provider
 * so that only ONE fetch to /api/stream is opened regardless of how many
 * components subscribe to SSE events.
 *
 * Usage:
 *   // In App.js (once, above AlertsProvider):
 *   <EventStreamProvider>...</EventStreamProvider>
 *
 *   // In any component:
 *   const { subscribe, isLive } = useEventStream();
 */
import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { API_BASE } from '../services/apiClient';
import { useAuth } from './AuthContext';
import { ConsumerUnitContext } from './ConsumerUnitContext';

const USE_MOCK = process.env.REACT_APP_MOCK === 'true';
const MAX_BACKOFF_MS = 30_000;

const EventStreamContext = createContext(null);

export function EventStreamProvider({ children }) {
  const { getToken } = useAuth();
  // Safe access — EventStreamProvider may mount before ConsumerUnitProvider
  const consumerCtx = useContext(ConsumerUnitContext);
  const selectedUnitId = consumerCtx?.selectedUnitId || 'all';
  const [isLive, setIsLive] = useState(false);
  const listenersRef = useRef(new Map()); // Map<eventType, Set<callback>>
  const abortRef = useRef(null);
  const retryCountRef = useRef(0);
  const mountedRef = useRef(true);

  const subscribe = useCallback((eventType, callback) => {
    const map = listenersRef.current;
    if (!map.has(eventType)) map.set(eventType, new Set());
    map.get(eventType).add(callback);

    return () => {
      const set = map.get(eventType);
      if (set) {
        set.delete(callback);
        if (set.size === 0) map.delete(eventType);
      }
    };
  }, []);

  const dispatch = useCallback((eventType, data) => {
    const set = listenersRef.current.get(eventType);
    if (!set) return;
    for (const cb of set) {
      try { cb(data); } catch { /* listener error — swallow */ }
    }
  }, []);

  useEffect(() => {
    if (USE_MOCK) return;
    mountedRef.current = true;

    let timeoutId = null;

    async function connect() {
      if (!mountedRef.current) return;

      const token = typeof getToken === 'function' ? getToken() : null;
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const unitParam = encodeURIComponent(selectedUnitId || 'all');
        const response = await fetch(`${API_BASE}/api/stream?unitId=${unitParam}`, {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const reader = response.body?.getReader();
        if (!reader) throw new Error('No response body');

        setIsLive(true);
        retryCountRef.current = 0;

        const decoder = new TextDecoder();
        let buffer = '';
        let currentEvent = null;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() ?? '';

          for (const line of lines) {
            if (line.startsWith('event: ')) {
              currentEvent = line.slice(7).trim();
            } else if (line.startsWith('data: ')) {
              const jsonStr = line.slice(6);
              if (currentEvent && jsonStr) {
                try {
                  const data = JSON.parse(jsonStr);
                  dispatch(currentEvent, data);
                } catch { /* malformed JSON — skip */ }
              }
              currentEvent = null;
            } else if (line === '' || line.startsWith(':')) {
              currentEvent = null;
            }
          }
        }
      } catch (err) {
        if (err.name === 'AbortError') return;
      }

      if (!mountedRef.current) return;
      setIsLive(false);
      const delay = Math.min(1000 * 2 ** retryCountRef.current, MAX_BACKOFF_MS);
      retryCountRef.current++;
      timeoutId = setTimeout(connect, delay);
    }

    connect();

    return () => {
      mountedRef.current = false;
      if (abortRef.current) abortRef.current.abort();
      if (timeoutId) clearTimeout(timeoutId);
      setIsLive(false);
    };
  }, [getToken, selectedUnitId, dispatch]);

  const value = React.useMemo(() => ({ isLive, subscribe }), [isLive, subscribe]);

  return (
    <EventStreamContext.Provider value={value}>
      {children}
    </EventStreamContext.Provider>
  );
}

// Safe fallback when outside provider (e.g. in tests that don't wrap with EventStreamProvider)
const FALLBACK = { isLive: false, subscribe: () => () => {} };

/**
 * useEventStream — returns { isLive, subscribe } from the shared SSE context.
 * Drop-in replacement for the old per-component hook.
 * Returns a no-op fallback when called outside EventStreamProvider (safe for tests).
 */
export function useEventStream() {
  const ctx = useContext(EventStreamContext);
  return ctx || FALLBACK;
}
